/**
 * Utility functions for extracting and handling API error messages.
 * Works with RTK Query's FetchBaseQueryError shape and the backend's
 * standard error response formats:
 *   - { error: "message" }   (from Content(HttpStatusCode.X, new { error = ... }))
 *   - { message: "message" } (from BadRequest("message"))
 *   - { Message: "message" } (ASP.NET default serialisation)
 */

/**
 * Extracts a human-readable error message from an RTK Query error object.
 * Returns the server-provided message when available, otherwise falls back
 * to the provided default message.
 *
 * @param error - The error object from RTK Query (FetchBaseQueryError shape).
 * @param fallback - The fallback message when no server message is available.
 * @returns A user-facing error message string.
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;

  const err = error as {
    status?: number | string;
    data?: { error?: string; message?: string; Message?: string };
  };

  // HTTP error with a JSON body
  if (typeof err.status === 'number' && err.data) {
    const serverMessage = err.data.error || err.data.message || err.data.Message;
    if (serverMessage) return serverMessage;
  }

  // RTK Query network-level errors
  if (err.status === 'FETCH_ERROR') {
    return 'Network error. Check your connection and try again.';
  }
  if (err.status === 'TIMEOUT_ERROR') {
    return 'The request timed out. Please try again.';
  }
  if (err.status === 'PARSING_ERROR') {
    return 'Received an unexpected response from the server. Please try again.';
  }

  return fallback;
}
